"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type WalletClient,
} from "viem";
import { parseUnits } from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import {
  EIP712Domain,
  Pair,
  withSignatureErrorHandler,
} from "#/utils/signature";

import { withdraw } from "../kyan.service";
import {
  type SignedUserWithdraw,
  type WithdrawRequest,
  type WithdrawResponse,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const UserWithdraw = [
  { name: "deadline", type: "uint256" },
  { name: "to", type: "address" },
  { name: "from", type: "address" },
  { name: "amount", type: "uint256" },
  { name: "pair", type: "Pair" },
];

const signWithdraw = async (
  withdrawRequest: Omit<WithdrawRequest, "signature" | "signature_deadline">,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<SignedUserWithdraw> => {
  const signature_deadline = getSignatureDeadline();

  return {
    ...withdrawRequest,
    signature_deadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { UserWithdraw, Pair },
      primaryType: "UserWithdraw",
      message: {
        deadline: signature_deadline,
        to: withdrawRequest.to,
        from: withdrawRequest.from,
        amount: parseUnits(withdrawRequest.amount.toString(), 6),
        pair: withdrawRequest.pair,
      },
    }),
  };
};

const validateWithdrawSignature = async (
  payload: SignedUserWithdraw,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return await publicClient.verifyTypedData({
    address: payload.from,
    domain: EIP712Domain[chainId],
    types: { UserWithdraw, Pair },
    primaryType: "UserWithdraw",
    message: {
      deadline: payload.signature_deadline,
      to: payload.to,
      from: payload.from,
      amount: parseUnits(payload.amount.toString(), 6),
      pair: payload.pair,
    },
    signature: payload.signature,
  });
};

export const useWithdraw = (
  options?: UseMutationOptions<
    WithdrawResponse,
    Error,
    Pick<WithdrawRequest, "to" | "from" | "amount" | "pair">
  > & {
    onPendingWithdraw?: (signedWithdraw: SignedUserWithdraw) => void;
  },
) => {
  const { walletClient } = useWalletClient();

  return useMutation({
    ...options,
    mutationFn: async ({ to, from, amount, pair }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const signedWithdraw = await withSignatureErrorHandler(async () =>
        signWithdraw(
          {
            to,
            from,
            amount,
            pair,
          },
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        ),
      );

      const isSignatureValid = await validateWithdrawSignature(
        signedWithdraw,
        walletClient.chain?.id as ChainId,
      );

      options?.onPendingWithdraw?.(signedWithdraw);

      return defaultApiResponseHandler({
        serviceName: "Withdraw",
        payload: signedWithdraw,
        service: withdraw,
        validation: [isSignatureValid],
      });
    },
  });
};
